import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dealer,
  useDeleteDealer,
  useEnableDealer,
  useDisableDealer,
} from "./useDealers";
import { trackEvent } from "utils/analytics";
import { notify } from "utils/notify";

interface UseDealersActionsReturn {
  // Dialog states
  addDealerDialogVisible: boolean;
  deleteDealerDialogVisible: boolean;
  enableDealerDialogVisible: boolean;
  disableDealerDialogVisible: boolean;
  selectedDealer: Dealer | null;

  // Loading states
  isDeleting: boolean;
  isEnabling: boolean;
  isDisabling: boolean;

  // Dialog handlers
  openAddDealerDialog: () => void;
  closeAddDealerDialog: () => void;
  openEnableDealerDialog: (dealer: Dealer) => void;
  closeEnableDealerDialog: () => void;
  openDisableDealerDialog: (dealer: Dealer) => void;
  closeDisableDealerDialog: () => void;
  closeDeleteDealerDialog: () => void;

  // Action handlers
  handleDeleteDealer: () => void;
  handleEnableDealer: () => void;
  handleDisableDealer: () => void;
  handleRowClick: (dealer: Dealer) => void;
}

export const useDealersActions = (): UseDealersActionsReturn => {
  // Dialog visibility state
  const [addDealerDialogVisible, setAddDealerDialogVisible] = useState(false);
  const [deleteDealerDialogVisible, setDeleteDealerDialogVisible] =
    useState(false);
  const [enableDealerDialogVisible, setEnableDealerDialogVisible] =
    useState(false);
  const [disableDealerDialogVisible, setDisableDealerDialogVisible] =
    useState(false);
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Use the dealer mutation hooks
  const { mutate: deleteDealer, isPending: isDeleting } = useDeleteDealer();
  const { mutate: enableDealer, isPending: isEnabling } = useEnableDealer();
  const { mutate: disableDealer, isPending: isDisabling } = useDisableDealer();

  // Dialog handlers
  const openAddDealerDialog = () => {
    trackEvent("dealer_add_started");
    setAddDealerDialogVisible(true);
  };

  const closeAddDealerDialog = () => {
    setAddDealerDialogVisible(false);
  };

  const closeDeleteDealerDialog = () => {
    setDeleteDealerDialogVisible(false);
    setSelectedDealer(null);
  };

  const openEnableDealerDialog = (dealer: Dealer) => {
    setSelectedDealer(dealer);
    setEnableDealerDialogVisible(true);
  };

  const closeEnableDealerDialog = () => {
    setEnableDealerDialogVisible(false);
    setSelectedDealer(null);
  };

  const openDisableDealerDialog = (dealer: Dealer) => {
    setSelectedDealer(dealer);
    setDisableDealerDialogVisible(true);
  };

  const closeDisableDealerDialog = () => {
    setDisableDealerDialogVisible(false);
    setSelectedDealer(null);
  };

  // Action handlers
  const handleDeleteDealer = () => {
    if (!selectedDealer) return;

    deleteDealer(selectedDealer.id, {
      onSuccess: () => {
        notify.success(
          `${selectedDealer.firstName} ${selectedDealer.lastName} has been permanently deleted.`
        );
        closeDeleteDealerDialog();
        queryClient.invalidateQueries({ queryKey: ["dealers"] });
      },
      onError: (error: any) => {
        notify.error(
          error?.message || "Failed to delete dealer. Please try again."
        );
      },
    });
  };

  const handleEnableDealer = () => {
    if (!selectedDealer) return;

    enableDealer(selectedDealer.id, {
      onSuccess: () => {
        trackEvent("dealer_status_changed", { action: "enabled" });
        notify.success(
          `${selectedDealer.firstName} ${selectedDealer.lastName} has been successfully enabled.`
        );
        closeEnableDealerDialog();
        queryClient.invalidateQueries({ queryKey: ["dealers"] });
      },
      onError: (error: any) => {
        notify.error(
          error?.message || "Failed to enable dealer. Please try again."
        );
      },
    });
  };

  const handleDisableDealer = () => {
    if (!selectedDealer) return;

    disableDealer(selectedDealer.id, {
      onSuccess: () => {
        trackEvent("dealer_status_changed", { action: "disabled" });
        notify.success(
          `${selectedDealer.firstName} ${selectedDealer.lastName} has been successfully disabled.`
        );
        closeDisableDealerDialog();
        queryClient.invalidateQueries({ queryKey: ["dealers"] });
      },
      onError: (error: any) => {
        notify.error(
          error?.message || "Failed to disable dealer. Please try again."
        );
      },
    });
  };

  const handleRowClick = (dealer: Dealer) => {
    navigate(`/dealers/${dealer.id}`);
  };

  return {
    // Dialog states
    addDealerDialogVisible,
    deleteDealerDialogVisible,
    enableDealerDialogVisible,
    disableDealerDialogVisible,
    selectedDealer,

    // Loading states
    isDeleting,
    isEnabling,
    isDisabling,

    // Dialog handlers
    openAddDealerDialog,
    closeAddDealerDialog,
    openEnableDealerDialog,
    closeEnableDealerDialog,
    openDisableDealerDialog,
    closeDisableDealerDialog,
    closeDeleteDealerDialog,

    // Action handlers
    handleDeleteDealer,
    handleEnableDealer,
    handleDisableDealer,
    handleRowClick,
  };
};
